/**
 * The local decrypted index.
 *
 * Gift wraps expose nothing to the relay, so no search, sort or count can
 * happen server-side. The client syncs, decrypts and indexes here, which is the
 * same shape Proton Mail uses and the price paid for end-to-end encryption.
 *
 * Nothing in here leaves the browser.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Mail, Disposition } from "../nostr/seal";
import type { Profile } from "../nostr/profile";
import type { WorkflowEntry } from "../nostr/workflow";
import { entryIsClean } from "../nostr/workflow";
import type { Draft } from "../ui/draft";
import { startsConversation } from "./thread";

// The name keeps the old product name. A new name would start every browser
// on an empty store and re-decrypt all mail from the relay.
const DB_NAME = "halimail";
// 2: `disposition` was denormalised to the top level. The index keyPath had
// always been "disposition" while the value lived at verdict.disposition, so no
// record ever entered the index: counts were right and every list was empty.
const DB_VERSION = 2;

interface MailDB extends DBSchema {
  mail: {
    key: string;
    value: StoredMail;
    indexes: {
      "by-disposition": string;
      "by-date": number;
      "by-sender": string;
    };
  };
  meta: { key: string; value: unknown };
}

export interface StoredMail extends Mail {
  /**
   * Where this message lives, denormalised to the top level.
   *
   * IndexedDB indexes a keyPath, and a record missing that path is simply
   * absent from the index rather than an error — so this must stay a real
   * field, not a nested one, and must be written by `toStored` every time.
   */
  disposition: Disposition;
  /** Lowercased sender address, for allowlist checks and grouping. */
  senderAddress: string;
  /** Everything searchable, lowercased and flattened once at write time. */
  searchText: string;
  /** Epoch ms, so sorting never parses a date string. */
  dateMs: number;
  read: boolean;
  replyLater: boolean;
  setAside: boolean;
  /**
   * Epoch seconds after which this message resurfaces at the top.
   *
   * Bubble Up is decided by the client and nothing else: no server scheduler
   * wakes for it, the date simply passes while the tab is shut, and the next
   * load puts the message where it will be seen. Undefined means unbubbled;
   * records written before Phase 5 carry no field, which is the same thing.
   */
  bubbleTo?: number | undefined;
  /**
   * A copy of a message this account sent.
   *
   * It belongs to a conversation and never to a box, so it is held, indexed and
   * searchable, and no box lists it. The conversation view is #29.
   */
  sent: boolean;
  /**
   * Deleted on screen, and waiting for the relay to hear it.
   *
   * Every read leaves it out, so the delete is instant and survives a reload;
   * the plaintext stays until the published list lands, so a delete that
   * fails can put the message back. `ui/deletes.ts` owns the rest.
   */
  deleting?: boolean | undefined;
}

/** What every list, count and search may show. */
function shown(item: StoredMail): boolean {
  return item.deleting !== true;
}

let db: IDBPDatabase<MailDB> | null = null;

export async function database(): Promise<IDBPDatabase<MailDB>> {
  if (db) return db;
  db = await openDB<MailDB>(DB_NAME, DB_VERSION, {
    upgrade(database, oldVersion) {
      // Records written before v2 have no top-level disposition and cannot be
      // repaired in place, so the store is rebuilt and the sync marker dropped;
      // everything is re-fetched and re-decrypted from the relay.
      if (oldVersion > 0 && database.objectStoreNames.contains("mail")) {
        database.deleteObjectStore("mail");
      }
      if (oldVersion > 0 && database.objectStoreNames.contains("meta")) {
        database.deleteObjectStore("meta");
      }
      const mail = database.createObjectStore("mail", { keyPath: "id" });
      // Kept and no longer read: `listable` decides which box shows a message,
      // and a sent copy's answer is not a field an index can hold. Dropping it
      // is a version bump, which rebuilds the store and re-syncs everything.
      mail.createIndex("by-disposition", "disposition");
      mail.createIndex("by-date", "dateMs");
      mail.createIndex("by-sender", "senderAddress");
      database.createObjectStore("meta");
    },
  });
  return db;
}

/** Address out of a `Name <addr>` header, lowercased. */
export function addressOf(header: string): string {
  const angled = /<([^>]+)>/.exec(header);
  const raw = (angled?.[1] ?? header).trim().toLowerCase();
  return raw.includes("@") ? raw : "";
}

/**
 * The display name on a `Name <addr>` header, empty when there is none.
 *
 * A header whose name is just the address again — several providers write one —
 * carries no name at all, and saying so here is what stops the reader printing
 * the same string twice.
 */
export function displayName(from: string): string {
  const match = /^\s*(.*?)\s*<[^>]*>\s*$/.exec(from ?? "");
  const name = (match?.[1] ?? "").replace(/^["']|["']$/g, "").trim();
  return name.toLowerCase() === addressOf(from) ? "" : name;
}

/**
 * Names by address, gathered from every message held.
 *
 * The allowlist is addresses and nothing else, so the mail itself is the only
 * contact information this client has: a sender who signed one message
 * `Sarah Jenkins` is Sarah Jenkins on the next one too, even when that one
 * arrives with a bare address. Newest wins, because a name can change.
 */
export async function senderNames(): Promise<Record<string, string>> {
  const d = await database();
  const names: Record<string, string> = {};
  const all = (await d.getAll("mail")).sort((a, b) => b.dateMs - a.dateMs);
  for (const item of all) {
    const name = displayName(item.from);
    if (name && item.senderAddress && !names[item.senderAddress]) {
      names[item.senderAddress] = name;
    }
  }
  return names;
}

/** Markup reduced to its words, which is all a substring search needs. */
function wordsOf(html: string): string {
  return html
    .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ");
}

function searchTextOf(mail: Mail): string {
  return [
    mail.from, mail.sender ?? "", mail.subject, mail.text ?? "", wordsOf(mail.html ?? ""),
    mail.to?.join(" ") ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

export function toStored(mail: Mail): StoredMail {
  // A message this account sent was never classified. It is filed in the Inbox
  // so it is never in the Screener's index, and `listable` decides whether a
  // box shows it.
  const disposition: Disposition =
    mail.verdict?.disposition ?? (mail.sent === true ? "inbox" : "screener");
  const searchable = searchTextOf(mail);
  return {
    ...mail,
    disposition,
    verdict: { ...(mail.verdict ?? { disposition }), disposition },
    senderAddress: mail.origin === "nostr" ? (mail.sender ?? "") : addressOf(mail.from),
    searchText: searchable,
    dateMs: Date.parse(mail.date) || mail.sealedAt * 1000,
    read: mail.sent === true,
    replyLater: false,
    setAside: false,
    bubbleTo: undefined,
    sent: mail.sent === true,
  };
}

export async function put(mail: StoredMail): Promise<void> {
  const d = await database();
  const tx = d.transaction("mail", "readwrite");
  // Never clobber local state (read, workflow flags) when a message is re-synced.
  const existing = await tx.store.get(mail.id);
  await tx.store.put(existing
    ? {
        ...mail,
        read: existing.read,
        replyLater: existing.replyLater,
        setAside: existing.setAside,
        bubbleTo: existing.bubbleTo,
        deleting: existing.deleting,
      }
    : mail);
  await tx.done;
}

/**
 * Every wrap id already held.
 *
 * The relay is re-read across a two-day skew window on every boot, so most of
 * what comes back is already here. Knowing that before opening a wrap saves two
 * signer round-trips per message — which on a phone signer is the whole cost.
 */
export async function knownIds(): Promise<Set<string>> {
  const d = await database();
  return new Set(await d.getAllKeys("mail"));
}

export async function putMany(items: StoredMail[]): Promise<void> {
  for (const item of items) await put(item);
}

/**
 * Every message held, newest first.
 *
 * Used to answer questions the indexes cannot: which address is this account's
 * own, and what name has each sender used.
 */
export async function allMail(): Promise<StoredMail[]> {
  const d = await database();
  return (await d.getAll("mail")).filter(shown).sort((a, b) => b.dateMs - a.dateMs);
}

/** Every box a message can be counted in. The four boxes plus quarantine. */
const BOXES: Disposition[] = ["inbox", "feed", "paper_trail", "screener", "quarantine"];

/**
 * Whether one box lists one message.
 *
 * A sent copy carries no verdict, so it would otherwise file itself into the
 * Screener and ask the person whether to let themselves in. It belongs to a
 * conversation instead — except when it starts one, which has no message above
 * it to sit under, so the Inbox lists it as a card marked as yours.
 *
 * Listing and counting share this one answer. Counting one way and listing
 * another is what left an empty list under a non-zero count.
 */
export function listable(item: StoredMail, disposition: Disposition, nowMs = Date.now()): boolean {
  if (!shown(item) || waiting(item, nowMs)) return false;
  if (item.sent) return disposition === "inbox" && startsConversation(item);
  return item.disposition === disposition;
}

/**
 * Whether a decision holds a message out of every box.
 *
 * Follow Up moves a message to the Follow Up stack, so the box no longer lists
 * it. Set Aside with a date holds it until that date, then the box lists it
 * again as new. A set-aside message with no date was written before Set Aside
 * took a date: no date can return it, so it stays listed.
 */
function waiting(item: StoredMail, nowMs: number): boolean {
  if (item.replyLater) return true;
  return item.setAside && !!item.bubbleTo && !isSurfaced(item, nowMs);
}

export async function byDisposition(disposition: Disposition, nowMs = Date.now()): Promise<StoredMail[]> {
  const d = await database();
  const all = await d.getAll("mail");
  return all.filter((m) => listable(m, disposition, nowMs)).sort((a, b) => b.dateMs - a.dateMs);
}

export async function countByDisposition(nowMs = Date.now()): Promise<Record<string, number>> {
  const d = await database();
  const counts: Record<string, number> = {};
  for (const item of await d.getAll("mail")) {
    for (const box of BOXES) if (listable(item, box, nowMs)) counts[box] = (counts[box] ?? 0) + 1;
  }
  return counts;
}

/** Substring search over the flattened text. Runs entirely locally. */
export async function search(query: string): Promise<StoredMail[]> {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const d = await database();
  const hits = (await d.getAll("mail")).filter((m) => shown(m) && m.searchText.includes(needle));
  return hits.sort((a, b) => b.dateMs - a.dateMs);
}

/**
 * Change one stored message, read and written in one transaction.
 *
 * A read and a put in separate transactions let another writer's snapshot land
 * between them, and whichever writes last silently undoes the other — a sync
 * once put back a message that was being deleted. Every row change goes
 * through here or holds its own readwrite transaction for the same reason.
 */
async function update(id: string, change: (item: StoredMail) => StoredMail): Promise<void> {
  const d = await database();
  const tx = d.transaction("mail", "readwrite");
  const item = await tx.store.get(id);
  if (item) await tx.store.put(change(item));
  await tx.done;
}

/**
 * Keep a body fetched from the blob store, and index its words.
 *
 * Search covers an offloaded body only after the reader opens it once.
 */
export async function foldBody(id: string, body: { html?: string; text?: string }): Promise<void> {
  await update(id, (item) => {
    const folded = {
      ...item,
      ...(body.html ? { html: body.html } : {}),
      ...(body.text ? { text: body.text } : {}),
    };
    return { ...folded, searchText: searchTextOf(folded) };
  });
}

export async function markRead(id: string, read = true): Promise<void> {
  await update(id, (item) => ({ ...item, read }));
}

export async function setFlag(
  id: string,
  flag: "replyLater" | "setAside" | "bubbleTo",
  value: boolean | number | undefined,
): Promise<void> {
  await update(id, (item) => ({ ...item, [flag]: value }));
}

/**
 * Make the held mail say what the map says, and report what moved.
 *
 * The event is the authority: an id the map no longer names is settled, so its
 * flags clear here. `changed` is what the caller re-publishes after applying a
 * remote map — nothing changes on the relay by applying, so a device that heard
 * another's decision must not re-broadcast it as its own newest stamp.
 */
export async function applyWorkflow(
  flags: Readonly<Record<string, WorkflowEntry>>,
): Promise<number> {
  const d = await database();
  const tx = d.transaction("mail", "readwrite");
  let changed = 0;
  for (const item of await tx.store.getAll()) {
    const entry = flags[item.id.toLowerCase()] ?? {};
    const next = {
      replyLater: entry.later === true,
      setAside: entry.aside === true,
      bubbleTo: entry.bubbleTo || undefined,
    };
    if (
      item.replyLater === next.replyLater
      && item.setAside === next.setAside
      && item.bubbleTo === next.bubbleTo
    ) continue;
    await tx.store.put({ ...item, ...next });
    changed += 1;
  }
  await tx.done;
  return changed;
}

/**
 * The map as this device's held mail reads it, for publishing.
 *
 * Clean messages hold no entry, so a year of mail costs a handful of ids.
 */
export async function workflowFlags(): Promise<Record<string, WorkflowEntry>> {
  const d = await database();
  const out: Record<string, WorkflowEntry> = {};
  for (const item of await d.getAll("mail")) {
    const entry: WorkflowEntry = {};
    if (item.replyLater) entry.later = true;
    if (item.setAside) entry.aside = true;
    if (item.bubbleTo) entry.bubbleTo = item.bubbleTo;
    if (!entryIsClean(entry)) out[item.id] = entry;
  }
  return out;
}

/**
 * Spam that has sat in the Spam box past its week.
 *
 * Measured from when ingest sealed it, never from the Date header: a spammer
 * writes that header, and an old one would delete a message the moment it
 * arrived.
 */
export async function expiredSpam(cutoffSeconds: number): Promise<string[]> {
  const d = await database();
  return (await d.getAll("mail"))
    .filter((m) => shown(m) && m.disposition === "quarantine" && m.sealedAt < cutoffSeconds)
    .map((m) => m.id);
}

/** Messages in the Follow Up stack, newest first. */
export async function followUps(): Promise<StoredMail[]> {
  return flagged("replyLater");
}

/**
 * Whether a bubble has surfaced: its date has passed.
 *
 * A surfaced bubble pins its message to the top of its box until the reader
 * opens it — resurfacing it forever would teach the person to ignore it.
 */
export function isSurfaced(item: StoredMail, nowMs: number): boolean {
  return !!item.bubbleTo && item.bubbleTo * 1000 <= nowMs;
}

/**
 * Whether the list shows a message as new: not read yet, or surfaced.
 *
 * A surfaced message returns as new even when it was read before, because it
 * came back so that the person sees it again. The read mark is held in this
 * browser only, so the bubble date is what every device agrees on.
 */
export function isNew(item: StoredMail, nowMs: number): boolean {
  return !item.read || isSurfaced(item, nowMs);
}

export async function flagged(flag: "replyLater" | "setAside"): Promise<StoredMail[]> {
  const d = await database();
  return (await d.getAll("mail")).filter((m) => shown(m) && m[flag]).sort((a, b) => b.dateMs - a.dateMs);
}

/**
 * What was decided about a sender in the Screener.
 *
 * Kept next to the mail rather than in the contact list because the two answers
 * are not symmetrical: "in" is published so postrd files future mail, while
 * "out" is purely local — the relay is never told who was refused.
 */
export type Screening = "in" | "out";

export async function screenings(): Promise<Record<string, Screening>> {
  const d = await database();
  return ((await d.get("meta", "screening")) as Record<string, Screening>) ?? {};
}

export async function recordScreening(address: string, decision: Screening): Promise<void> {
  const d = await database();
  const all = await screenings();
  await d.put("meta", { ...all, [address.trim().toLowerCase()]: decision }, "screening");
}

/**
 * Move one sender's mail out of `from` and into `to`, verdict and index
 * together. Scoped to a source box so a screening decision cannot drag a
 * newsletter out of the Feed.
 */
export async function refileSender(
  address: string,
  from: Disposition,
  to: Disposition,
): Promise<void> {
  const d = await database();
  const tx = d.transaction("mail", "readwrite");
  for (const item of await tx.store.index("by-sender").getAll(address.trim().toLowerCase())) {
    if (item.disposition !== from) continue;
    await tx.store.put({ ...item, disposition: to, verdict: { ...item.verdict, disposition: to } });
  }
  await tx.done;
}

/** Take a message off the screen while its delete is published. */
export async function hide(id: string): Promise<void> {
  await update(id, (item) => ({ ...item, deleting: true }));
}

/** Put back messages whose delete never reached the relay. */
export async function unhide(ids: readonly string[]): Promise<void> {
  for (const id of ids) await update(id, (item) => ({ ...item, deleting: false }));
}

/** The delete queue: everything hidden and not yet published. */
export async function hidden(): Promise<string[]> {
  const d = await database();
  return (await d.getAll("mail")).filter((m) => !shown(m)).map((m) => m.id);
}

/**
 * Delete one message here, and remember that it is deleted.
 *
 * The relay keeps the ciphertext: a gift wrap is signed by a throwaway key
 * nobody holds, so no account can withdraw one. The id therefore goes on a list
 * this client keeps, and the sync never opens that wrap again. The list is
 * published so a second browser deletes it too — `nostr/deleted.ts`.
 */
export async function remove(id: string): Promise<void> {
  const d = await database();
  await d.delete("mail", id);
  await rememberDeleted([id]);
}

export async function deletedIds(): Promise<string[]> {
  const d = await database();
  return ((await d.get("meta", "deleted")) as string[] | undefined) ?? [];
}

/** Merge ids into the local list. A delete made anywhere is a delete here. */
export async function rememberDeleted(ids: readonly string[]): Promise<void> {
  const d = await database();
  const merged = new Set(await deletedIds());
  for (const id of ids) if (id) merged.add(id.trim().toLowerCase());
  await d.put("meta", [...merged].sort(), "deleted");
}

/** Drop the plaintext of everything already deleted. Used after a sync. */
export async function purgeDeleted(ids: readonly string[]): Promise<number> {
  const d = await database();
  const held = new Set(await d.getAllKeys("mail"));
  const gone = ids.filter((id) => held.has(id));
  if (gone.length === 0) return 0;
  const tx = d.transaction("mail", "readwrite");
  for (const id of gone) tx.store.delete(id);
  await tx.done;
  return gone.length;
}

/** Drop every message from one sender. Used when a sender is screened out. */
export async function forgetSender(address: string): Promise<void> {
  const d = await database();
  const keys = await d.getAllKeysFromIndex("mail", "by-sender", address.trim().toLowerCase());
  const tx = d.transaction("mail", "readwrite");
  for (const key of keys) tx.store.delete(key);
  await tx.done;
}

/**
 * Blob plaintext, kept once opened.
 *
 * Keyed by the ciphertext digest, which is what the ref names, so the same body
 * fetched from two messages is stored once. This is the difference between one
 * approval on a phone signer and one per reopen — and it is plaintext, so it
 * goes when the key does.
 */
export async function cachedBlob(sha256: string): Promise<Uint8Array | undefined> {
  const d = await database();
  const stored = (await d.get("meta", `blob:${sha256}`)) as ArrayBuffer | Uint8Array | undefined;
  if (!stored) return undefined;
  return stored instanceof Uint8Array ? stored : new Uint8Array(stored);
}

export async function cacheBlob(sha256: string, bytes: Uint8Array): Promise<void> {
  const d = await database();
  await d.put("meta", bytes, `blob:${sha256}`);
}

/**
 * The account's avatar, cached by the URL it came from.
 *
 * Keyed by URL so a changed picture replaces itself, and stored as bytes
 * because the page renders it from a blob rather than from a remote `src`.
 */
export async function cachedAvatar(url: string): Promise<Uint8Array | undefined> {
  const d = await database();
  const stored = (await d.get("meta", "avatar")) as
    { url: string; bytes: ArrayBuffer | Uint8Array } | undefined;
  if (!stored || stored.url !== url) return undefined;
  return stored.bytes instanceof Uint8Array ? stored.bytes : new Uint8Array(stored.bytes);
}

export async function cacheAvatar(url: string, bytes: Uint8Array): Promise<void> {
  const d = await database();
  await d.put("meta", { url, bytes }, "avatar");
}

/**
 * The account's own profile, cached.
 *
 * Kept so the bar draws the right name and face on the first frame after a
 * reload rather than a placeholder that is replaced a second later. It is
 * cleared with everything else on sign-out.
 */
export async function cachedProfile(): Promise<Profile | undefined> {
  const d = await database();
  return (await d.get("meta", "profile")) as Profile | undefined;
}

export async function cacheProfile(profile: Profile): Promise<void> {
  const d = await database();
  await d.put("meta", profile, "profile");
}

/**
 * What is being written.
 *
 * A draft is plaintext the person has not sent, so it lives with the decrypted
 * index and goes when the key does. It is keyed by the message it answers, so
 * pressing Reply twice returns to what was written the first time.
 */
export async function saveDraft(draft: Draft): Promise<void> {
  const d = await database();
  await d.put("meta", draft, `draft:${draft.id}`);
}

export async function loadDraft(id: string): Promise<Draft | undefined> {
  const d = await database();
  return (await d.get("meta", `draft:${id}`)) as Draft | undefined;
}

export async function deleteDraft(id: string): Promise<void> {
  const d = await database();
  await d.delete("meta", `draft:${id}`);
}

export async function drafts(): Promise<Draft[]> {
  const d = await database();
  const keys = (await d.getAllKeys("meta")).filter(
    (key) => typeof key === "string" && key.startsWith("draft:"),
  );
  const out: Draft[] = [];
  for (const key of keys) {
    const draft = (await d.get("meta", key)) as Draft | undefined;
    if (draft) out.push(draft);
  }
  return out.sort((a, b) => b.updated - a.updated);
}

/**
 * The draft the composer had open, so a reload brings it back.
 *
 * Kept apart from the drafts themselves: a draft that is merely held must not
 * reopen the composer over the mail, and one that was on screen must.
 */
export async function composing(): Promise<string | undefined> {
  const d = await database();
  return (await d.get("meta", "composing")) as string | undefined;
}

export async function setComposing(id: string | null): Promise<void> {
  const d = await database();
  if (id === null) await d.delete("meta", "composing");
  else await d.put("meta", id, "composing");
}

/**
 * A workflow decision made here and not yet published.
 *
 * Kept on disk so a reload or sign-out inside the publish delay does not lose
 * it, and so the next sync publishes it before the relay's map can overwrite
 * it.
 */
export async function workflowPending(): Promise<boolean> {
  const d = await database();
  return (await d.get("meta", "workflowPending")) === true;
}

export async function setWorkflowPending(pending: boolean): Promise<void> {
  const d = await database();
  await d.put("meta", pending, "workflowPending");
}

/** Whether the retired delete list has been withdrawn, once per account. */
export async function deletesMigrated(): Promise<boolean> {
  const d = await database();
  return (await d.get("meta", "deletesMigrated")) === true;
}

export async function setDeletesMigrated(): Promise<void> {
  const d = await database();
  await d.put("meta", true, "deletesMigrated");
}

/** Newest sealedAt seen, so a resync fetches only what is new. */
export async function lastSync(): Promise<number | undefined> {
  const d = await database();
  return (await d.get("meta", "lastSync")) as number | undefined;
}

export async function setLastSync(seconds: number): Promise<void> {
  const d = await database();
  await d.put("meta", seconds, "lastSync");
}

/** Wipe everything. Used on sign-out: the key leaves, the plaintext goes too. */
export async function clearAll(): Promise<void> {
  const d = await database();
  await d.clear("mail");
  await d.clear("meta");
}
